"use client";

import Image from "next/image";
import { NextLink } from "@/components/ui/next-link";
import dynamic from "next/dynamic";
import type { SerializedEditorState } from "lexical";
import { ArrowRight, MessageSquare } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/ui/user-avatar";
import { ClanTagBadge } from "@/components/ui/clan-tag-badge";
import { News } from "@/lib/types";
import { formatDateTimeShort } from "@/lib/format";

const Editor = dynamic(() => import("@/components/editor/editor").then((m) => m.Editor), { ssr: false });

function NewsContent({ content }: { content: string }) {
  let parsed: SerializedEditorState | undefined;
  try {
    const json = JSON.parse(content);
    if (json?.root) parsed = json as SerializedEditorState;
  } catch {}

  if (parsed) {
    return <Editor editorSerializedState={parsed} readOnly />;
  }
  return <p className="text-sm whitespace-pre-wrap">{content}</p>;
}

const AnnouncementCard = ({ news_id, news_title, news_content, news_time, news_banner, news_banner_position, name, author_image, author_clan_tag, comments_count }: News) => (
  <Card key={news_id} className="overflow-hidden">
    {news_banner && (
      <div className="w-full h-[300px] relative">
        <Image src={news_banner} alt={news_title} fill className="object-cover" style={{ objectPosition: news_banner_position ?? "50% 50%" }} />
      </div>
    )}
    <CardContent className="p-6 space-y-4">
      <div>
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold ">{news_title}</h1>
          <h2 className="text-xl font-bold">{formatDateTimeShort(news_time as string)}</h2>
        </div>
        <div className="flex items-center gap-2">
          <UserAvatar name={name} image={author_image} className="h-14 w-14" />
          <p className="text-xl text-primary flex items-center gap-2">
            {name}
            <ClanTagBadge tag={author_clan_tag} />
          </p>
        </div>
      </div>
      <div className=" relative max-h-[200px] min-h-[150px] overflow-hidden line-clamp-300">
        <NewsContent content={news_content} />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex h-16 items-end justify-center bg-gradient-to-t from-card via-card-background/80 to-transparent p-18"></div>
      </div>
      <div className="flex justify-end pt-2">
        {comments_count != 0 && (
          <NextLink href={`/home/news/${news_id}`}>
            <h2 className="text-xl font-semibold flex items-center gap-2 pr-1">
              {" "}
              <MessageSquare className="h-5 w-5" />
              {/* (99) Kommentare */}
              Kommentare ({comments_count})
            </h2>
          </NextLink>
        )}
        <Button asChild variant="outline" size="sm" className="gap-1.5">
          <NextLink href={`/home/news/${news_id}`}>
            {" "}
            Weiterlesen
            <ArrowRight className="h-3.5 w-3.5" />
          </NextLink>
        </Button>
      </div>
    </CardContent>
  </Card>
);

export function NewsList({ newsList }: { newsList: News[] }) {
  return (
    <div className="space-y-4">
      {newsList.map((newsItem) => (
        <AnnouncementCard key={newsItem.news_id} {...newsItem} />
      ))}
    </div>
  );
}
